import {
  Page,
  Content,
  InfoCard,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { ClientDataTable } from '@WESCO-International/wdp-ui-components/components/table';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { domainOptions, getSystemOptionsForDomain } from '../common/utils';
import { Input } from '@WESCO-International/wdp-ui-components/components/input';
import { FaSearch } from 'react-icons/fa';

export const DomainSystemsPage = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const domainKey = (domain ?? '').toLowerCase();
  const domainMeta = domainOptions.find(
    d => (d.value ?? '').toLowerCase() === domainKey,
  );

  const systems = useMemo(
    () =>
      getSystemOptionsForDomain(domainMeta?.value).map(s => ({
        system: s.label,
        owner: s.owner ?? 'guest',
        description: s.description ?? '-',
        value: s.value,
      })),
    [domainMeta?.value],
  );

  const filteredSystems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return systems;
    return systems.filter(s => {
      return (
        String(s.system ?? '')
          .toLowerCase()
          .includes(term) ||
        String(s.owner ?? '')
          .toLowerCase()
          .includes(term) ||
        String(s.description ?? '')
          .toLowerCase()
          .includes(term)
      );
    });
  }, [searchTerm, systems]);

  const columns = [
    {
      key: 'system',
      name: 'System',
      formatter: ({ row }: { row: any }) => (
        <a
          href="#"
          className="link"
          onClick={e => {
            e.preventDefault();
            navigate(
              `/domains/${encodeURIComponent(
                domainMeta?.value ?? '',
              )}/systems/${encodeURIComponent(row.value)}`,
            );
          }}
        >
          {row.system}
        </a>
      ),
      width: 220,
    },
    { key: 'owner', name: 'Owner', width: 140 },
    { key: 'description', name: 'Description' },
  ];

  if (!domainMeta) {
    return (
      <Content>
        <ResponseErrorPanel error={new Error('Domain not found.')} />
      </Content>
    );
  }

  return (
    <Page themeId="home">
      <Content>
        <div className="pageHeader">
          <div className="wdp-heading s-5 semibold headingLarge">
            <div className="backTitleContainer">
              <a
                href="#"
                className="link backLink"
                aria-label="Back"
                title="Back"
                onClick={e => {
                  e.preventDefault();
                  navigate('/domains');
                }}
              >
                ←
              </a>
              <span className="wdp-heading s-5 semibold headingLarge">
                {domainMeta.label}
              </span>
            </div>
          </div>
        </div>
        <InfoCard
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <span className="cardTitleSmall">
                System ({filteredSystems.length})
              </span>
              <div style={{ minWidth: 220, maxWidth: 320 }}>
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e: any) => setSearchTerm(e.target.value)}
                  iconLeft={<FaSearch />}
                />
              </div>
            </div>
          }
        >
          <ClientDataTable
            columns={columns}
            rows={filteredSystems}
            paginate
            resize={true}
            dense={false}
            rowHeight={42}
            headerRowHeight={42}
            verticalBorder={false}
          />
        </InfoCard>
      </Content>
    </Page>
  );
};
